// src\components\TVDisplayView.jsx
import React, { useEffect, useState, useRef } from 'react';
import TVDashboard from './dashboard/TVDashboard';
import { useData } from '../contexts/DataContext';

/**
 * Vue optimisée pour l'affichage sur téléviseur grand format
 * Conçu pour une rotation automatique sans interaction utilisateur
 */
const TVDisplayView = () => {
  const { loading, error, loadAllData } = useData();
  const [fullscreen, setFullscreen] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const refreshTimerRef = useRef(null);

  // Effet pour charger les données initiales une seule fois
  useEffect(() => {
    const initialLoad = async () => {
      await loadAllData();
      setInitialLoadComplete(true);
    };

    initialLoad();
    
    // Nettoyage
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, []); // Dépendance vide pour n'exécuter qu'au montage

  // Configurer un rechargement périodique APRÈS le chargement initial
  useEffect(() => {
    if (initialLoadComplete && !refreshTimerRef.current) {
      // Configurer un rechargement périodique (toutes les 5 minutes)
      refreshTimerRef.current = setInterval(() => {
        loadAllData();
      }, 5 * 60 * 1000);
    }
    
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [initialLoadComplete, loadAllData]);

  // Fonctionnalité de plein écran
  const toggleFullscreen = () => {
    if (!fullscreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if (document.documentElement.mozRequestFullScreen) {
        document.documentElement.mozRequestFullScreen();
      } else if (document.documentElement.webkitRequestFullscreen) {
        document.documentElement.webkitRequestFullscreen();
      } else if (document.documentElement.msRequestFullscreen) {
        document.documentElement.msRequestFullscreen();
      }
      setFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setFullscreen(false);
    }
  };

  // Écouter les changements d'état du plein écran
  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Si les données sont en cours de chargement initial
  if (loading && !initialLoadComplete) {
    return (
      <div className="flex justify-center items-center h-screen bg-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-xl text-blue-800">Chargement des données de production...</p>
        </div>
      </div>
    );
  }

  // Si une erreur s'est produite
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-red-50">
        <div className="bg-white border-l-4 border-red-500 text-red-700 p-8 rounded-lg shadow-lg max-w-2xl">
          <h1 className="text-2xl font-bold mb-4">Erreur de chargement des données</h1>
          <p className="text-xl">{error}</p>
          <p className="mt-4 text-lg">Veuillez contacter le service informatique au poste 4200.</p>
          <button 
            className="mt-6 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={() => {
              setInitialLoadComplete(false);
              loadAllData().then(() => setInitialLoadComplete(true));
            }}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-gray-100 flex flex-col">
      {/* Bouton de plein écran (uniquement visible avant le plein écran) */}
      {!fullscreen && (
        <div className="fixed top-2 right-2 z-50">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow hover:bg-blue-700 flex items-center"
            onClick={toggleFullscreen}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
            </svg>
            Mode Plein Écran
          </button>
        </div>
      )}
      
      {/* Dashboard TV */}
      <div className="flex-grow">
        <TVDashboard />
      </div>
      
      {/* Pied de page avec instructions */}
      {!fullscreen && (
        <div className="bg-blue-800 text-white p-2 text-center">
          Cliquez sur "Mode Plein Écran" pour afficher en plein écran. Appuyez sur la touche ESC pour quitter.
        </div>
      )}

      {/* Indicateur de mise à jour des données */}
      {loading && initialLoadComplete && (
        <div className="fixed bottom-4 left-4 bg-blue-100 border border-blue-400 text-blue-700 px-4 py-2 rounded shadow-md">
          Mise à jour des données en cours...
        </div>
      )}
    </div>
  );
};

export default TVDisplayView;